import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck, Chrome } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { formatApiErrorDetail } from "@/utils/apiError";

export default function AdminLogin() {
  const { t } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.role === "employee" ? "/employee" : "/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-heading text-xl font-bold">{t.admin.login}</h1>
            <p className="text-sm text-muted-foreground">{t.admin.loginSubtitle}</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="mt-8 grid gap-5" data-testid="admin-login-form">
          {error && (
            <p data-testid="admin-login-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="grid gap-2">
            <Label htmlFor="admin-email">{t.admin.email}</Label>
            <Input
              id="admin-email"
              data-testid="admin-login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="admin-password">{t.admin.password}</Label>
            <Input
              id="admin-password"
              data-testid="admin-login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <Button type="submit" data-testid="admin-login-button" disabled={loading} className="gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t.admin.signIn}
          </Button>
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">{t.admin.or}</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <Button
            type="button"
            variant="outline"
            data-testid="google-signin-button"
            onClick={googleSignIn}
            className="gap-2"
          >
            <Chrome className="h-4 w-4" />
            {t.admin.googleSignIn}
          </Button>
          <div className="flex items-center justify-between text-sm">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-primary hover:underline">
              {t.admin.forgot}
            </Link>
            <Link to="/" data-testid="back-home-link" className="text-muted-foreground hover:text-foreground">
              {t.admin.backHome}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
